import React from "react";
import { NavDropdown } from "react-bootstrap";
import { DropdownSubmenu } from "react-bootstrap-submenu";
import "./MultiLevelDropdownMenu.styles.scss";
import { usePathname, useRouter } from "next/navigation";
import HoverDropdownSubmenu from "./HoverableDropdownMenu";
import { MenuRoute, MenuRouteItem } from "./types";
import { BsChevronDown } from "react-icons/bs";
import { PrefetchNavDropdownItem } from "./PrefetchNavDropdownItem";
import { isSameRouteNavigation } from "@/components/navigation/isSameRouteNavigation";

const isExternalLink = (url: string): boolean => {
    return /^https?:\/\//.test(url);
};

// This recursive function will handle any nesting depth
function renderMenuItems(
    items: MenuRoute[] | null | undefined,
    currentPath: string | null,
    isHome: boolean = false
) {
    if (!items) return null;

    return items
        .filter(
            (item) =>
                !!item.to ||
                (Array.isArray(item.children) && item.children.length > 0)
        )
        .map((item, idx) => {
            const hasChildren = item.children && item.children.length > 0;
            const onlyChild = hasChildren && item.children?.length === 1;
            const child = onlyChild ? item.children?.[0] : null;
            const shouldMerge = onlyChild && child?.name === item.name;
            const isActive = determineIsActive(item, currentPath) && !isHome;
            const openInNewTab = item.openInNewTab === true;
            const isExternalLinkOnly = isExternalLink(item.to || "#");
            const isExternal = isExternalLinkOnly || openInNewTab;

            // Special case handling for single item with same name
            if (shouldMerge) {
                const openMergedInNewTab = child?.openInNewTab === true;
                const isMergedExternalLink = isExternalLink(child?.to || "#");
                const isMergedExternal =
                    openMergedInNewTab || isMergedExternalLink;
                if (isMergedExternal) {
                    return (
                        <NavDropdown.Item
                            href={child?.to || "#"}
                            key={item.name || idx}
                            active={isActive}
                            target={
                                openMergedInNewTab || isMergedExternalLink
                                    ? "_blank"
                                    : "_self"
                            }
                            rel={
                                openMergedInNewTab || isMergedExternalLink
                                    ? "noopener noreferrer"
                                    : undefined
                            }
                        >
                            {item.name}
                        </NavDropdown.Item>
                    );
                }
                return (
                    <PrefetchNavDropdownItem
                        href={child?.to || "#"}
                        key={item.name || idx}
                        active={isActive}
                        target={
                            openMergedInNewTab || isMergedExternalLink
                                ? "_blank"
                                : undefined
                        }
                        rel={
                            openMergedInNewTab || isMergedExternalLink
                                ? "noopener noreferrer"
                                : undefined
                        }
                    >
                        {item.name}
                    </PrefetchNavDropdownItem>
                );
            }

            if (hasChildren) {
                return (
                    <DropdownSubmenu
                        title={item.name}
                        href={item.to || "#"}
                        key={item.name || idx}
                        className={`dropdown-submenu-children${isActive ? " active" : ""}`}
                    >
                        {/* This recursive call supports unlimited nesting */}
                        {renderMenuItems(item.children, currentPath)}
                    </DropdownSubmenu>
                );
            }

            // Recursive rendering for items with children
            if (isExternal) {
                return (
                    <NavDropdown.Item
                        href={item.to || "#"}
                        key={item.name || idx}
                        active={isActive}
                        target={
                            openInNewTab || isExternalLinkOnly
                                ? "_blank"
                                : "_self"
                        }
                        rel={
                            openInNewTab || isExternalLinkOnly
                                ? "noopener noreferrer"
                                : undefined
                        }
                    >
                        {item.name}
                    </NavDropdown.Item>
                );
            }

            return (
                <PrefetchNavDropdownItem
                    href={item.to || "#"}
                    key={item.name || idx}
                    active={isActive}
                    target={
                        openInNewTab || isExternalLinkOnly
                            ? "_blank"
                            : undefined
                    }
                    rel={
                        openInNewTab || isExternalLinkOnly
                            ? "noopener noreferrer"
                            : undefined
                    }
                >
                    {item.name}
                </PrefetchNavDropdownItem>
            );
        });
}

function determineIsActive(
    item: MenuRoute,
    currentPath: string | null
): boolean {
    if (item.to === currentPath) return true;
    if (item.children && item.children.length > 0) {
        return item.children.some((child) =>
            determineIsActive(child, currentPath)
        );
    }
    return false;
}

// Reusable multi-level dropdown menu component
export const MultiLevelNavDropdownMenu: React.FC<{ menu: MenuRouteItem }> = ({
    menu,
}) => {
    const currentPath = usePathname() ?? "";
    const router = useRouter();

    const hasChildren =
        Array.isArray(menu.children) && menu.children.length > 0;
    const onlyChild =
        hasChildren && menu?.children !== null && menu?.children.length === 1;
    const child = onlyChild ? menu?.children?.[0] : null;
    const shouldMerge = onlyChild && child?.name === menu.label;
    const isHome = currentPath === "/";

    // Do not render if no 'to' and no children
    if (
        !("to" in menu && typeof menu.to === "string" && menu.to) &&
        !hasChildren
    ) {
        return null;
    }

    // If menu has a 'to' and no children, render as a styled nav link (no dropdown)
    if (
        "to" in menu &&
        typeof menu.to === "string" &&
        menu.to &&
        !hasChildren
    ) {
        const openInNewTab = menu.openInNewTab === true;
        const isExternalLinkOnly = isExternalLink(menu.to);
        return (
            <li className="nav-item single-menu">
                <a
                    href={menu.to}
                    className={`nav-link${currentPath === menu.to ? " active" : ""}`}
                    style={{
                        color: "#fff",
                        fontWeight: 500,
                        fontSize: "1rem",
                        padding: "0.5rem 1.5rem",
                        display: "flex",
                        alignItems: "center",
                        background: "transparent",
                    }}
                    onClick={(e) => {
                        if (openInNewTab || isExternalLinkOnly) {
                            return;
                        }
                        e.preventDefault();
                        if (
                            isSameRouteNavigation(
                                menu.to as string,
                                window.location.href
                            )
                        ) {
                            return;
                        }
                        router.push(menu.to as string);
                    }}
                    target={
                        openInNewTab || isExternalLinkOnly
                            ? "_blank"
                            : undefined
                    }
                    rel={
                        openInNewTab || isExternalLinkOnly
                            ? "noopener noreferrer"
                            : undefined
                    }
                >
                    {menu.label}
                </a>
            </li>
        );
    } else if (!hasChildren) {
        return (
            <li className="nav-item single-menu">
                <button
                    type="button"
                    className="nav-link"
                    style={{
                        color: "#fff",
                        fontWeight: 500,
                        fontSize: "1rem",
                        padding: "0.5rem 1.5rem",
                        display: "flex",
                        alignItems: "center",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                    }}
                    disabled
                >
                    {menu.label}
                </button>
            </li>
        );
    }

    if (shouldMerge) {
        const openMergedInNewTab = child?.openInNewTab === true;
        const isMergedExternalLink = isExternalLink(child?.to || "#");
        const isExternal = isMergedExternalLink || openMergedInNewTab;

        if (isExternal) {
            return (
                <NavDropdown.Item
                    href={child?.to}
                    active={child !== undefined}
                    target={
                        openMergedInNewTab || isMergedExternalLink
                            ? "_blank"
                            : "_self"
                    }
                    rel={
                        openMergedInNewTab || isMergedExternalLink
                            ? "noopener noreferrer"
                            : undefined
                    }
                >
                    {menu.label}
                </NavDropdown.Item>
            );
        }

        return (
            <PrefetchNavDropdownItem
                href={child?.to || "#"}
                active={child !== undefined}
                target={
                    openMergedInNewTab || isMergedExternalLink
                        ? "_blank"
                        : undefined
                }
                rel={
                    openMergedInNewTab || isMergedExternalLink
                        ? "noopener noreferrer"
                        : undefined
                }
            >
                {menu.label}
            </PrefetchNavDropdownItem>
        );
    }

    if (hasChildren) {
        return (
            <HoverDropdownSubmenu
                title={
                    <span style={{ display: "flex", alignItems: "center" }}>
                        {menu.label}
                        <BsChevronDown
                            className="chevron-rotate"
                            style={{ marginLeft: 6, fontSize: 8 }}
                        />
                    </span>
                }
                href={"#"}
                key={"_"}
            >
                {renderMenuItems(menu.children, currentPath, isHome)}
            </HoverDropdownSubmenu>
        );
    }

    return null;
};
